import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Bell, BellSlash } from "@phosphor-icons/react";
import { isPushSupported, getPushStatus, subscribePush, unsubscribePush } from "@/lib/push";

/**
 * Small pill button that lets the user opt-in to push notifications.
 * Placed in the top bar. Silent if the browser doesn't support push.
 */
export default function PushToggle() {
  const [status, setStatus] = useState("unknown");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!(await isPushSupported())) { setStatus("unsupported"); return; }
    setStatus(await getPushStatus());
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, []);

  if (status === "unsupported" || status === "unknown") return null;

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      toast.success("Push notifications enabled");
      refresh();
    } catch (e) { toast.error(e.message || "Could not enable notifications"); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      toast.success("Push notifications disabled");
      refresh();
    } catch (e) { toast.error(e.message || "Could not disable"); }
    finally { setBusy(false); }
  };

  const subscribed = status === "subscribed";
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={subscribed ? disable : enable}
      disabled={busy}
      className="gap-1.5"
      data-testid="push-toggle"
      title={subscribed ? "Notifications ON — click to disable" : "Enable notifications"}
    >
      {subscribed ? <Bell size={16} weight="fill" className="text-primary" /> : <BellSlash size={16} className="text-muted-foreground" />}
      <span className="hidden md:inline text-xs">{subscribed ? "On" : "Notify me"}</span>
    </Button>
  );
}
